import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { GET_ALL_USER_KPIS, ADD_USER_KPI } from "../api/kpi";
import { generateUuid } from "../utils/generateUuid";
import { types } from "../redux/kpi/types";

// ---------------------------------------------------------------------------
// Query key factory — unique per (month, workspace)
// ---------------------------------------------------------------------------
export const pastMonthKeys = {
  scores: (monthKey, workspaceId) => ["past-month-scores", monthKey, workspaceId],
};

// ---------------------------------------------------------------------------
// Internal: transform GET_ALL_USER_KPIS response into Redux historicKpis shape.
// Mirrors the GetPreviousKpis saga logic so UseKpiScoresTable renders unchanged.
// ---------------------------------------------------------------------------
function toReduxShape(rawData, monthKey) {
  const { scores = [], users: sharedUsers = [] } = rawData || {};

  const userDays = {};
  const sharedDays = [];

  scores.forEach((score) => {
    if (score.scope === "User") {
      userDays[score.ownerId] = [...(score.days || [])];
    } else {
      sharedDays.push(...(score.days || []));
    }
  });

  // Distribute shared KPI scores to every user in the workspace
  if (sharedDays.length) {
    sharedUsers.forEach((uid) => {
      if (!userDays[uid]) userDays[uid] = [];
      userDays[uid].push(...sharedDays);
    });
  }

  const usersKpis = Object.entries(userDays).map(([ownerId, days]) => {
    const month = { total: 0 };
    days.forEach((day) => {
      Object.entries(day.kpis || {}).forEach(([kpiId, score]) => {
        if (month[kpiId]) {
          month[kpiId].pts += score?.pts ?? 0;
          month[kpiId].amt += score?.amt ?? 0;
        } else {
          month[kpiId] = { pts: score?.pts ?? 0, amt: score?.amt ?? 0 };
        }
        month.total += score?.pts ?? 0;
      });
    });
    return { userId: ownerId, month };
  });

  usersKpis.sort((a, b) => b.month.total - a.month.total);
  return { [monthKey]: usersKpis };
}

// ---------------------------------------------------------------------------
// usePastMonthKpisQuery
//
// Fetches past-month scores via TanStack Query — completely outside Redux.
// A useEffect syncs the result into Redux historicKpis (GET_PREVIOUS_KPIS_SUCCESS)
// so UseKpiScoresTable and displayedGroupKpis continue to work without changes.
//
// NOTE: we use useEffect instead of the query's onSuccess callback because
// onSuccess in TanStack Query v4 has known StrictMode / concurrent-render
// issues where it may silently not fire.
//
// dataUpdatedAt is included so a refetch still re-syncs into Redux even when
// query.data is referentially equal (e.g. after REFRESH_KPIS wiped historicKpis).
//
// Safe to call with monthKey=null — the query stays disabled.
// ---------------------------------------------------------------------------
export function usePastMonthKpisQuery(monthKey, workspaceId, companyId) {
  const dispatch = useDispatch();

  const query = useQuery(
    pastMonthKeys.scores(monthKey, workspaceId),
    async () => {
      const res = await GET_ALL_USER_KPIS(companyId, workspaceId, monthKey);
      return res.data;
    },
    {
      enabled: !!monthKey && !!workspaceId && !!companyId,
      staleTime: 30_000,
    }
  );

  // Sync TanStack data into Redux whenever it changes (initial load or after
  // mutation invalidation). Fires reliably in all React render modes.
  useEffect(() => {
    if (query.data && monthKey) {
      dispatch({
        type: types.GET_PREVIOUS_KPIS_SUCCESS,
        historicKpis: toReduxShape(query.data, monthKey),
        monthRef: monthKey,
      });
    }
  }, [query.data, query.dataUpdatedAt, monthKey, dispatch]);

  return query;
}

// ---------------------------------------------------------------------------
// usePastMonthSaveMutation
//
// Saves a KPI score for a past month (incremental add via ADD_USER_KPI).
// On success: optimistic historic merge + invalidate TanStack cache.
// Does NOT call refreshKpis() — that clears historicKpis and blanks Scorekeeper.
// ---------------------------------------------------------------------------
export function usePastMonthSaveMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation(
    async (variables) => {
      const { kpiId, userId, monthKey, data, companyId, workspaceId } = variables;

      if (!kpiId || !userId || !monthKey || !companyId || !workspaceId) {
        throw new Error("Missing required fields for past-month KPI save");
      }
      if (!/^\d{4}-\d{2}$/.test(monthKey)) {
        throw new Error(`Invalid monthKey format: ${monthKey}`);
      }

      const entryId = generateUuid();
      const res = await ADD_USER_KPI({
        kpiId,
        companyId,
        userId,
        month: monthKey,
        entryId,
        data,
      });
      if (res.error) throw new Error(res.error.message);
      return res.data;
    },
    {
      onSuccess: (saved, variables) => {
        const amount = Number(saved?.amount) || 0;
        const points = Number(saved?.points) || 0;
        dispatch({
          type: types.ADD_PAST_MONTH_KPI_SUCCESS,
          monthKey: variables.monthKey,
          userId: variables.userId,
          kpiId: variables.kpiId,
          amount,
          points,
        });
        queryClient.invalidateQueries(
          pastMonthKeys.scores(variables.monthKey, variables.workspaceId)
        );
      },
    }
  );
}
